import type { FC, ReactNode } from "react";
import { Button, MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { SimpleMenu } from "../Menu/simpleMenu.js";
import { SimpleMenuItem } from "../Menu/simpleMenuItem.js";
import { SimpleMenuPopover } from "../Menu/simpleMenuPopover.js";
import {
  TeammateLabel,
  type TeammateLabelProps,
} from "../teammate/teammateLabel.js";

export interface CustomerTimelineRowDetailsParticipant extends TeammateLabelProps {
  id: string;
  gradingSummary?: ReactNode;
  outcome?: ReactNode;
}

export interface CustomerTimelineRowDetailsProps {
  title: ReactNode;
  participants: readonly CustomerTimelineRowDetailsParticipant[];
  /**
   * Content after the title, such as a call's length. It stays on the title's
   * last line. It gets the title's text styles, so pass it already styled.
   */
  trailingAccessory?: ReactNode;
  /**
   * Pressing the title or its accessory opens the row; with several
   * participants, a menu first asks whose grading to open. Without it, the
   * title is plain text, for a row with nothing to open.
   */
  onOpen?: (participantId: string) => void;
}

/**
 * A timeline row's details: its title, teammates and trailing accessory, for
 * a call, a text conversation or anything else on the row. It does no data
 * fetching and knows no routes.
 */
export const CustomerTimelineRowDetails: FC<CustomerTimelineRowDetailsProps> = (
  props,
) => {
  const { title, participants, trailingAccessory, onOpen } = props;
  const [onlyParticipant] = participants;
  const hasMultipleParticipants = participants.length > 1;
  // With several participants the menu opens the row instead.
  const openOnlyParticipant =
    onOpen && onlyParticipant && !hasMultipleParticipants
      ? () => onOpen(onlyParticipant.id)
      : undefined;
  const hasGradingMetadata = participants.some(
    (participant) =>
      participant.gradingSummary != null || participant.outcome != null,
  );

  // Two small no-break spaces put a gap between the title and the accessory.
  // Keep them. A regular space would let the accessory wrap onto a line by
  // itself. This only works while both are in one line of text, so the button
  // wraps both.
  const titleText = (
    <span className="whitespace-nowrap">
      <span
        className={twMerge(
          "whitespace-normal",
          onOpen &&
            "underline decoration-gray-300 underline-offset-2 transition-colors group-hover:decoration-gray-500",
        )}
      >
        {title}
      </span>
      {trailingAccessory ? (
        <>
          <span className="text-xs">{"\u00a0\u00a0"}</span>
          {trailingAccessory}
        </>
      ) : null}
    </span>
  );
  const titleButton = (
    <Button
      className="group block cursor-pointer rounded-sm text-left text-sm font-medium text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      onPress={openOnlyParticipant}
    >
      {titleText}
    </Button>
  );

  return (
    <div className="max-w-2xl min-w-0">
      {!onOpen ? (
        <p className="text-sm font-medium text-foreground">{titleText}</p>
      ) : hasMultipleParticipants ? (
        <MenuTrigger>
          {titleButton}
          <SimpleMenuPopover placement="bottom start">
            <SimpleMenu aria-label="Choose teammate grading">
              {participants.map((participant) => (
                <SimpleMenuItem
                  key={participant.id}
                  id={participant.id}
                  onAction={() => onOpen(participant.id)}
                >
                  {participant.name}
                </SimpleMenuItem>
              ))}
            </SimpleMenu>
          </SimpleMenuPopover>
        </MenuTrigger>
      ) : (
        titleButton
      )}

      {participants.length > 0 && (
        <div
          className={twMerge(
            "mt-2 flex text-xs text-muted-foreground",
            hasGradingMetadata
              ? "flex-col gap-y-2"
              : "flex-wrap gap-x-3 gap-y-1",
          )}
        >
          {participants.map((participant) => (
            <div
              key={participant.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1"
            >
              <TeammateLabel
                name={participant.name}
                kind={participant.kind}
                configurationName={participant.configurationName}
                isAvatarHidden={participant.isAvatarHidden}
              />
              {participant.gradingSummary}
              {participant.outcome}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
